import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Activity, MessageCircle } from "lucide-react";
import { coachMessagesUnreadCount } from "../lib/api";

const POLL_MS = 60 * 1000;

/**
 * CoachNav — mobile-only bottom tab bar for the coach ("app look", mirrors
 * the athlete portal nav): Home (Coaching Hub) · Recent Activity · Messages
 * (unread badge). Desktop keeps the existing BrandHeader top nav.
 */
export default function CoachNav() {
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const load = () =>
      coachMessagesUnreadCount()
        .then((d) => setUnread(d.total || 0))
        .catch(() => {});
    load();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [location.pathname]);

  const path = location.pathname;
  const items = [
    {
      key: "home",
      label: "Home",
      icon: Home,
      to: "/coaching",
      active:
        path.startsWith("/coaching") &&
        !path.startsWith("/coaching/activity") &&
        !path.startsWith("/coaching/messages"),
    },
    {
      key: "activity",
      label: "Activity",
      icon: Activity,
      to: "/coaching/activity",
      active: path.startsWith("/coaching/activity"),
    },
    {
      key: "messages",
      label: "Messages",
      icon: MessageCircle,
      to: "/coaching/messages",
      active: path.startsWith("/coaching/messages"),
      badge: unread,
    },
  ];

  return (
    <>
      {/* In-flow spacer so fixed bar never covers the last page content */}
      <div className="h-16 md:hidden" aria-hidden="true" />
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 flex items-stretch bg-white border-t border-[var(--tir-border)] shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="coach-nav-bottom"
      >
        {items.map((it) => {
          const Icon = it.icon;
          const activeCls = it.active
            ? "text-[var(--tir-burgundy)]"
            : "text-[var(--tir-muted)] hover:text-[var(--tir-ink)]";
          return (
            <Link
              key={it.key}
              to={it.to}
              data-testid={`coach-nav-${it.key}`}
              className={`relative flex flex-col flex-1 py-2 items-center justify-center transition-colors ${activeCls}`}
            >
              <span className="relative">
                <Icon size={20} strokeWidth={it.active ? 2.4 : 2} />
                {it.badge > 0 && (
                  <span
                    className="absolute -top-1.5 -right-2 min-w-[15px] h-[15px] px-0.5 bg-[var(--tir-burgundy)] text-white text-[8px] font-bold rounded-full flex items-center justify-center leading-none"
                    data-testid="coach-nav-messages-badge"
                  >
                    {it.badge > 9 ? "9+" : it.badge}
                  </span>
                )}
              </span>
              <span className="text-[9px] mt-1 tracking-[0.18em] uppercase font-semibold">
                {it.label}
              </span>
            </Link>
          );
        })}
      </nav>
    </>
  );
}
